/** @jsxImportSource @emotion/react */
import { Table, Tag, Typography } from '@quickbit/qb-design-system';
import { Link } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import QbPay from '../../images/qb-pay.png';
import { CodeBlock } from '../CodeBlock';
import { InlineCode } from '../InlineCode';
import { PageText } from './types';

const apiKeyExample = `{
  "X-Api-Key": "your-api-key-here"
}`;

const createPayOrderExample = `{
  "amount": 100.00,
  "currency": "EUR",
  "callback_url": "https://your-website.com/callback",
  "reference_id": "order-12345",
  "redirect_urls": {
    "success": "https://your-website.com/success",
    "failure": "https://your-website.com/failure",
    "refund": "https://your-website.com/refund"
}`;

const createPayOrderResponseExample = `{
  "uuid": "550e8400-e29b-41d4-a716-446655440000",
  "amount": 100.00,
  "creation_amount": 100.00,
  "currency": "EUR",
  "status": "PENDING",
  "created_at": "2024-01-01T12:00:00Z",
  "updated_at": "2024-01-01T12:00:00Z",
  "callback_url": "https://your-website.com/callback",
  "reference_id": "order-12345",
  "redirect_url": "https://your-website.com/success",
  "failure_redirect_url": "https://your-website.com/failure",
  "checkout_url": "https://qb-pay.quickbit.com?order-id=550e8400-e29b-41d4-a716-446655440000",
  "crypto_amount": null
}`;

const iframeExample = `<iframe
  src="https://qb-pay.quickbit.com?order-id=3fa85f64-5717-4562-b3fc-2c963f66afa6"
  allow="clipboard-write; clipboard-read"
/>`;

const callbackExample = `{
  "order_id": "3fa85f64-5717-4562-b3fc-2c963f66afa6",
  "type": "NOTICED",
  "reference_id": "9013AA"
}`;

const cryptoAmountExample = `{
    "amount": 123456,
    "currency_code": "BTC",
    "scale": 8
}`;

const cryptoAmountUsageExample = `const order = createOrder(/* input data */); // Assuming this function exists and creates an order
const cryptoAmount = order.crypto_amount;

if (cryptoAmount) {
  const { amount, currency_code, scale } = cryptoAmount;
  const formattedCryptoAmount = amount * Math.pow(10, -scale);
  console.log(\`Crypto Amount: \${formattedCryptoAmount} \${currency_code}\`); // Crypto Amount: 0.00123456 BTC
}
`;

const getOrderResponseExample = `{
  "amount": 100,
  "crypto_amount": null,
  "currency": "EUR",
  "uuid": "3fa85f64-5717-4562-b3fc-2c963f66afa6",
  "created_at": "2023-04-12T06:23:07.558Z",
  "checkout_url": "https://qb-pay.quickbit.com?order-id=3fa85f64-5717-4562-b3fc-2c963f66afa6",
  "reference_id": "9013AA",
  "status": "COMPLETED",
  "creation_amount": 100,
  "errors": [],
  "email": null,
  "card_details": {
    "masked_card_number": "535583******1234",
    "account_holder": "Roger Quickbitsson"
  },
  "ssn_information": {
    "ssn": "198001011234",
    "country": "SE"
  }
}`;

const ordersSearchRequestBodyExample = `// Goes inside request body
{
  "pagination": {
    "page": 1,
    "size": 1
  },
  "filters": {
    "created_from": "2024-04-25T18:30:00.000Z",
    "created_to": "2024-04-30T18:30:00.000Z"
  }
}`;

const getAllOrdersResponseExample = `{
  "orders": [
    {
      "amount": 100,
      "crypto_amount": null,
      "currency": "EUR",
      "uuid": "3fa85f64-5717-4562-b3fc-2c963f66afa6",
      "created_at": "2023-04-12T06:23:07.558Z",
      "checkout_url": null,
      "reference_id": "9013AA",
      "status": "COMPLETED",
      "creation_amount": 100,
      "errors": [],
      "email": "roger.quickbitsson@quickbit.com",
      "card_details": {
        "masked_card_number": "535583******1234",
        "account_holder": "Roger Quickbitsson"
      },
      "ssn_information": {
        "ssn": "198001011234",
        "country": "SE"
      },
      "callback_url": "https://my-domain.com/api/quickbit-callback",
    },
  ],
  "aggregated_data": {
      "order_count": 1
  }
}`;

const cardExampleWithout3ds = `Card number: 5555 5555 5555 4444
Name: <Any name>
Expiry date: <Any future date>
CVV: <Any number>`;

const cardExampleWith3ds = `Card number: 4444 3333 2222 1111
Name: <Any name>
Expiry date: <Any future date>
CVV: <Any number>`;

const errorsList = `From Quickbit:
BLOCKCHAIN_CANCELLED - The crypto transaction was cancelled on the blockchain
PIQ_DEPOSIT_UNEXPECTED_ERROR - An error occurred when creating a buy request (using encrypted card details in the onramp flow)
PIQ_DEPOSIT_REQUEST_FAILED - The request didn't cause an exception, but failed when creating a buy request (using encrypted card details in the onramp flow)
PIQ_DEPOSIT_UNEXPECTED_TX_STATE - The deposit somehow ended up in a state that wasn't expected after creating a buy request (using encrypted card details in the onramp flow)
ORDER_TIMED_OUT - The order timed out according to the merchant configuration regarding how orders live.

From PaymentIQ:
ERR_SYSTEM_ERROR
ERR_FAILED_TO_CONNECT`;

export const payOrdersDocumentation: Array<PageText> = [
  {
    id: 'pay-orders-overview',
    title: 'Pay Orders',
    page: 'integration',
    subPage: 'pay-orders',
    content: <Typography variant="heading1">Pay Orders</Typography>,
  },
  {
    id: 'pay-orders-introduction',
    title: 'Pay Orders',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Typography variant="body">
          Pay Orders are the standard way to create cryptocurrency payment
          orders in Quickbit Pay. This flow is designed for customers who
          already have cryptocurrency and want to pay directly with it.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'pay-orders-when-to-use',
    title: 'When to Use Pay Orders',
    page: 'integration',
    subPage: 'pay-orders',
    content: <Typography variant="heading2">When to Use Pay Orders</Typography>,
  },
  {
    id: 'pay-orders-when-to-use-content',
    title: 'When to Use Pay Orders',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Typography variant="body">
          Use Pay Orders when your customers want to pay directly with
          cryptocurrency they already own. This is different from{' '}
          <Link to="/docs/integration/onramp">Onramp Orders</Link> where
          customers purchase cryptocurrency with fiat currency, or{' '}
          <Link to="/docs/integration/processing">Processing Orders</Link> which
          handle post-purchase processing.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'creating-pay-order',
    title: 'Creating a Pay Order',
    page: 'integration',
    subPage: 'pay-orders',
    content: <Typography variant="heading2">Creating a Pay Order</Typography>,
  },
  {
    id: 'creating-pay-order-endpoint',
    title: 'Creating a Pay Order',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Typography variant="body">
          Pay Orders are created using the{' '}
          <InlineCode>POST /api/pay/v1/orders/pay</InlineCode> endpoint. This
          endpoint requires authentication using your API key in the{' '}
          <InlineCode>X-Api-Key</InlineCode> header.
        </Typography>
        <CodeBlock language="json" code={apiKeyExample} />
        <br></br>
      </>
    ),
  },
  {
    id: 'pay-order-request-body',
    title: 'Request Body',
    page: 'integration',
    subPage: 'pay-orders',
    content: <Typography variant="subtitle">Request Body</Typography>,
  },
  {
    id: 'pay-order-request-body-content',
    title: 'Request Body',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Typography variant="body">
          The request body should contain the order details as JSON. Required
          fields include <InlineCode>amount</InlineCode> and{' '}
          <InlineCode>currency</InlineCode>, while{' '}
          <InlineCode>callback_url</InlineCode>,{' '}
          <InlineCode>reference_id</InlineCode>, and redirect URLs are optional
          but recommended.
        </Typography>
        <ul>
          <li>
            <InlineCode>amount</InlineCode> - The amount to charge (decimal)
          </li>
          <li>
            <InlineCode>currency</InlineCode> - The fiat currency (EUR, USD,
            etc.)
          </li>
          <li>
            <InlineCode>callback_url</InlineCode> - URL to receive order status
            updates (optional)
          </li>
          <li>
            <InlineCode>reference_id</InlineCode> - Your internal order ID
            (optional)
          </li>
          <li>
            <InlineCode>redirect_url</InlineCode> - Success redirect URL
            (optional)
          </li>
          <li>
            <InlineCode>failure_redirect_url</InlineCode> - Failure redirect URL
            (optional)
          </li>
        </ul>
        <br></br>
      </>
    ),
  },
  {
    id: 'pay-order-example',
    title: 'Example Request',
    page: 'integration',
    subPage: 'pay-orders',
    content: <Typography variant="subtitle">Example Request</Typography>,
  },
  {
    id: 'pay-order-example-content',
    title: 'Example Request',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Typography variant="body">
          Here is an example of creating a Pay Order for a 100 EUR purchase:
        </Typography>
        <CodeBlock language="json" code={createPayOrderExample} />
        <br></br>
      </>
    ),
  },
  {
    id: 'pay-order-response',
    title: 'Response',
    page: 'integration',
    subPage: 'pay-orders',
    content: <Typography variant="subtitle">Response</Typography>,
  },
  {
    id: 'pay-order-response-content',
    title: 'Response',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Typography variant="body">
          The response contains the created order details including a unique{' '}
          <InlineCode>uuid</InlineCode>, status, and{' '}
          <InlineCode>checkout_url</InlineCode> where customers can complete
          their payment.
        </Typography>
        <CodeBlock language="json" code={createPayOrderResponseExample} />
        <Typography variant="body">
          The <InlineCode>uuid</InlineCode> is the unique identifier for your
          order - save this ID as it will be used in callbacks and when
          retrieving order information.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'pay-order-checkout-flow',
    title: 'Checkout Flow',
    page: 'integration',
    subPage: 'pay-orders',
    content: <Typography variant="heading2">Checkout Flow</Typography>,
  },
  {
    id: 'pay-order-checkout-flow-content',
    title: 'Checkout Flow',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Typography variant="body">
          After creating a Pay Order, redirect your customer to the{' '}
          <InlineCode>checkout_url</InlineCode>. They will be able to select
          their preferred cryptocurrency and get a payment address. The checkout
          page supports both iframe embedding and full-page redirects.
        </Typography>
        <br></br>
        <Tag
          value="Recommended"
          iconName="info"
          variant="success"
          size="large"
        />
        <Typography variant="body">
          For the best user experience, embed the checkout page in an{' '}
          <InlineCode>iframe</InlineCode> with{' '}
          <InlineCode>clipboard-write</InlineCode> and{' '}
          <InlineCode>clipboard-read</InlineCode> permissions enabled.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'pay-order-vs-legacy',
    title: 'Migration from Legacy Endpoint',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <Typography variant="heading2">Migration from Legacy Endpoint</Typography>
    ),
  },
  {
    id: 'pay-order-vs-legacy-content',
    title: 'Migration from Legacy Endpoint',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Typography variant="body">
          If you are currently using the legacy{' '}
          <InlineCode>POST /api/pay/v1/order</InlineCode> endpoint for standard
          cryptocurrency payments, migrating to the new Pay Orders endpoint is
          straightforward. The request and response formats are nearly
          identical, with the main difference being the endpoint URL.
        </Typography>
        <br></br>
        <Typography variant="body">
          <strong>Legacy:</strong>{' '}
          <InlineCode>POST /api/pay/v1/order</InlineCode>
          <br />
          <strong>New:</strong>{' '}
          <InlineCode>POST /api/pay/v1/orders/pay</InlineCode>
        </Typography>
        <br></br>
        <Tag value="Important" iconName="info" variant="primary" size="large" />
        <Typography variant="body">
          The legacy endpoint will continue to work for backward compatibility,
          but we recommend migrating to the new endpoint for better clarity and
          future-proofing your integration.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'pay-order-crypto-amount',
    title: 'Understanding crypto amounts',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <Typography variant="heading2">Understanding crypto amounts</Typography>
    ),
  },
  {
    id: 'pay-order-crypto-amount-1',
    title: 'Understanding crypto amounts',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Typography variant="body">
          The order&apos;s <InlineCode>crypto_amount</InlineCode> field
          describes the amount in crypto that is expected to be paid for the
          order. That is, it is the <InlineCode>amount</InlineCode> field
          exchanged to the cryptocurrency. This might not be set initially, but
          will instead be available once a cryptocurrency has been chosen for
          the order. This is done by the customer on the checkout page.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'pay-order-crypto-amount-format',
    title: 'Crypto amount format',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Typography variant="body">
          The <InlineCode>crypto_amount</InlineCode> field is formatted a bit
          differently than the regular <InlineCode>amount</InlineCode> field. It
          is an object that contains the amount, the currency code, and the
          scale of the amount. The scale is used to determine how many decimal
          places the amount has. For instance, if the scale is 8 then the last
          digit of the amount should be in the 8th decimal place. See an example
          object below.
        </Typography>
        <CodeBlock language="json" code={cryptoAmountExample} />
        <Typography variant="body">
          The scale is applied to the amount by multiplying the amount with 10
          to the power of the negative scale. An example in JavaScript on how to
          apply this can be found below.
        </Typography>
        <CodeBlock language="javascript" code={cryptoAmountUsageExample} />
        <br></br>
      </>
    ),
  },
  {
    id: 'rendering-the-checkout-page',
    title: 'Rendering the checkout page',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <Typography variant="heading2">Rendering the checkout page</Typography>
    ),
  },
  {
    id: 'rendering-the-checkout-page-1',
    title: 'Rendering the checkout page',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Typography variant="body">
          As is shown above, the data returned from creating an order contains a
          checkout URL. This URL leads to a checkout page that the customer will
          use to send money. It allows the customer to select which
          cryptocurrency they want to use, and an address is then generated for
          them to send money to. This address is connected to the order, so the
          Quickbit Pay system will use it to determine that an order is
          processing or completed, and make sure the money gets sent your way.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'rendering-the-checkout-page-2',
    title: 'Rendering the checkout page',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <div
          css={{
            marginLeft: 'auto',
            marginRight: 'auto',
            display: 'block',
            textAlign: 'center',
          }}
        >
          <img
            css={{
              width: '300px',
              marginBottom: '8px',
            }}
            src={QbPay}
            alt="The Quickbit Pay checkout page"
          />
          <Typography variant="captionMedium">
            An example of the checkout page.
          </Typography>
        </div>
        <br></br>
      </>
    ),
  },
  {
    id: 'rendering-the-checkout-page-3',
    title: 'Rendering the checkout page',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Typography variant="body">
          As can be seen above, it is important to the checkout flow that the
          customer is redirected to the Quickbit Pay checkout page. This can be
          done in two ways: by using an <InlineCode>iframe</InlineCode> or by
          opening the page in a new window. The former of these two is
          recommended for Quickbit Pay orders because it allows the customer to
          stay inside your checkout ecosystem through the entire process.
          However, iframes are <b>not</b> supported in the Onramp Flow. For more
          information about <InlineCode>iframes</InlineCode>, see{' '}
          <a
            href="https://www.w3schools.com/tags/tag_iframe.ASP"
            target="_blank"
            rel="noreferrer"
          >
            this link
          </a>
          . For more information about the Onramp Flow, see{' '}
          <Link to="/docs/integration/onramp">this page</Link>.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'rendering-the-checkout-page-4',
    title: 'Rendering the checkout page',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Tag value="Note" iconName="info" variant="primary" size="large" />
        <Typography variant="body">
          For the rendered checkout page to work correctly in an{' '}
          <InlineCode>iframe</InlineCode>, make sure to allow the{' '}
          <InlineCode>clipboard-write</InlineCode> and{' '}
          <InlineCode>clipboard-read</InlineCode> options, as seen below.
        </Typography>
        <CodeBlock language="jsx" code={iframeExample} />
        <br></br>
      </>
    ),
  },
  {
    id: 'callback-events',
    title: 'Callback events',
    page: 'integration',
    subPage: 'pay-orders',
    content: <Typography variant="heading2">Callback events</Typography>,
  },
  {
    id: 'callback-events-1',
    title: 'Callback events',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Typography variant="body">
          The Quickbit Pay system will notify you on the specified{' '}
          <InlineCode>callback_url</InlineCode> when the order is either
          cancelled or completed. How and when this is done depends on how the
          customer pays for their purchase. If a customer uses the Quickbit app
          to complete their order, the callback event will be created as soon as
          they have sent the correct amount in the app. If they, on the other
          hand, use an external cryptocurrency wallet, the transaction will be
          sent out on the blockchain. Quickbit Pay monitors the blockchain for
          updates and will notify you with a callback event once we recognise
          the transaction on it. Therefore, it takes a little bit longer for
          orders to be completed if the customer sends crypto from outside the
          Quickbit custodial environment.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'callback-events-2',
    title: 'Callback events',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Typography variant="body">
          The callback event will contain a body with the following properties:
          the order ID, the callback type, as well as the reference ID if that
          was provided. An example body is shown below.
        </Typography>
        <CodeBlock language="json" code={callbackExample} />
        <Typography variant="body">
          The order ID is added for you to determine which order we are
          notifying you about. This will be the same ID as the{' '}
          <InlineCode>uuid</InlineCode> returned in the response when creating
          orders. Similarly, the reference ID is added (if you provided one) for
          the same reason.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'callback-events-3',
    title: 'Callback events',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Typography variant="body">
          Finally, the type of the callback determines what we are notifying you
          about. The following values are possible:
        </Typography>
        <ul>
          <li>
            <InlineCode>NOTICED</InlineCode> - This means we have noticed the
            transaction being sent on the blockchain. This callback will only be
            sent if the customer pays using an external wallet, i.e. not using
            the Quickbit app. In most cases, this means the money will correctly
            be sent to the Quickbit system (to then be settled into your bank
            account). This callback event can therefore be used to continue your
            checkout process and thereby reduce the time the customer has to
            wait on the blockchain. Do note, however, that there is a slight
            risk of the transaction not reaching its destination.
          </li>
          <li>
            <InlineCode>COMPLETED</InlineCode> - This means the money has
            reached the Quickbit system and will be sent to you in the next
            settlement. The remainder of your checkout can proceed without risk,
            such as sending a confirmation email or giving the product to the
            customer. If you specified a <InlineCode>redirect_url</InlineCode>{' '}
            when creating the order, the checkout page will redirect to that URL
            when this event is sent.
          </li>
          <li>
            <InlineCode>CANCELLED</InlineCode> - This means the order has been
            cancelled by the Quickbit Pay system. This can, for instance, be
            used to remove the reservation of a product in your system. It is
            possible to configure when orders should be cancelled using the
            Quickbit backoffice. More about this can be read{' '}
            <HashLink to="/docs/backoffice">here</HashLink>.
          </li>
          <li>
            <InlineCode>REFUNDED</InlineCode> - This means the order has been
            refunded by the Quickbit Pay system. This happens if the customer
            sent an invalid amount and provides an address for Quickbit Pay to
            send back the money to. It can be treated the same as the order
            being cancelled. More about this can be read in the{' '}
            <HashLink to="/docs/integration#refunds">Refunds</HashLink> section.
          </li>
          <li>
            <InlineCode>ERROR_OCCURRED</InlineCode> - This means the order
            received an error. This could depend on many different things, such
            as the customer using an invalid payment method or something going
            wrong on the blockchain. It should however be noted that an error
            occurring does not mean that the order has failed. It is possible
            for an order to have an error and still be completed successfully.
            The errors can be found in the <InlineCode>errors</InlineCode> field
            of the order. They can also be found in the Quickbit backoffice.
            More about this can be read in the{' '}
            <HashLink to="/docs/backoffice">Backoffice</HashLink> section.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'callback-events-4',
    title: 'Callback events',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Tag value="Note" iconName="info" variant="primary" size="large" />
        <Typography variant="body">
          When a callback is sent from the Quickbit Pay system, the order status
          will be the same status as the callback type unless it is the{' '}
          <InlineCode>ERROR_OCCURRED</InlineCode> type.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'simulating-callbacks-for-testing-purposes',
    title: 'Simulating callbacks for testing purposes',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <Typography variant="subtitle">
        Simulating callbacks for testing purposes
      </Typography>
    ),
  },
  {
    id: 'simulating-callbacks-for-testing-purposes-1',
    title: 'Simulating callbacks for testing purposes',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Typography variant="body">
          Since real blockchain addresses are used even in the test environment,
          it can be difficult to test the Quickbit Pay checkout flow. For that
          reason, we have added the possibility of simulating status changes for
          orders into the different callback states. This can be done in two
          ways: either by using the{' '}
          <InlineCode>
            {'POST /api/pay/v1/order/{order_id}/simulate-callback'}
          </InlineCode>{' '}
          simulation endpoint, or by using the backoffice to update the order.
          That is, you can send requests that will cause you to receive
          callbacks for an order, or you can use an interface to do it. This
          will also update the order, meaning that the Quickbit Pay checkout
          page will be updated depending on which status the order got instead.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'simulating-callbacks-for-testing-purposes-2',
    title: 'Simulating callbacks for testing purposes',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Typography variant="body">
          The simulation endpoint and backoffice actions are only available in
          the Quickbit Pay test environment.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'fetching-an-order',
    title: 'Fetching an order',
    page: 'integration',
    subPage: 'pay-orders',
    content: <Typography variant="heading2">Fetching an order</Typography>,
  },
  {
    id: 'fetching-an-order-1',
    title: 'Fetching an order',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Typography variant="body">
          In case an order needs to be retrieved in the future you can do so
          using the{' '}
          <InlineCode>{'GET /api/pay/v1/order/{order_id}'}</InlineCode>{' '}
          endpoint. As can be seen, the endpoint requires the order ID. This is
          yet another reason why it is important to store the{' '}
          <InlineCode>uuid</InlineCode> returned when creating orders.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'fetching-an-order-2',
    title: 'Fetching an order',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Typography variant="body">
          The endpoint for fetching orders will return the same type of response
          as the endpoint for creating orders. However, it may also include card
          details and or SSN information if access to this information is part
          of your contract with Quickbit, and if this information is available.
          An example can be seen below.
        </Typography>
        <CodeBlock language="json" code={getOrderResponseExample} />
        <br></br>
      </>
    ),
  },
  {
    id: 'fetching-all-orders',
    title: 'Fetching all orders',
    page: 'integration',
    subPage: 'pay-orders',
    content: <Typography variant="heading2">Fetching all orders</Typography>,
  },
  {
    id: 'fetching-all-orders-1',
    title: 'Fetching all orders',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Typography variant="body">
          If you wish to get all orders created from a certain time, you can do
          so using the <InlineCode>POST /api/pay/v1/orders/search</InlineCode>{' '}
          endpoint.
        </Typography>
        <br />
      </>
    ),
  },
  {
    id: 'fetching-all-orders-2',
    title: 'Fetching all orders',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Typography variant="body">
          The endpoint for fetching all orders supports pagination and filtering
          which means you can pass these options in a JSON body in the request.
          To make the response paginated, provide a{' '}
          <InlineCode>page</InlineCode> and <InlineCode>size</InlineCode> in a{' '}
          <InlineCode>pagination</InlineCode> object inside JSON body in
          request. The endpoint supports filtering the response based on
          creation time. In case you only need orders for a certain time period,
          provide a time in <InlineCode>filters</InlineCode> object inside the
          JSON body in the request via the <InlineCode>created_from</InlineCode>{' '}
          and <InlineCode>created_to</InlineCode> property. Alternatively you
          can pass only <InlineCode>created_from</InlineCode> to get all orders
          from a specific date and time, or <InlineCode>created_to</InlineCode>{' '}
          to get all orders up to a specific date and time. There are other
          filters that you can include. For more detailed information, see{' '}
          <Link to="/docs/endpoints">Endpoints</Link>. Below is an example of
          all the supported options which you can send in the request body.
        </Typography>
        <CodeBlock language="json" code={ordersSearchRequestBodyExample} />
        <br />
      </>
    ),
  },
  {
    id: 'fetching-all-orders-3',
    title: 'Fetching all orders',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Typography variant="body">
          The response is an object with an <InlineCode>orders</InlineCode> list
          containing all orders created by merchants under the same platform if
          a platform is making the request or all orders of a single merchant if
          a merchant is making the request. The{' '}
          <InlineCode>aggregated_data</InlineCode> contains information about
          the total number of orders. An example response can be found below.
        </Typography>
        <CodeBlock language="json" code={getAllOrdersResponseExample} />
        <br />
      </>
    ),
  },
  {
    id: 'paying-with-the-quickbit-app-in-the-test-environment',
    title: 'Paying with the Quickbit app in the test environment',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <Typography variant="heading2">
        Paying with the Quickbit app in the test environment
      </Typography>
    ),
  },
  {
    id: 'paying-with-the-quickbit-app-in-the-test-environment-1',
    title: 'Paying with the Quickbit app in the test environment',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Typography variant="body">
          As can be seen in the checkout page, it is possible to pay for orders
          using the Quickbit app. This app uses identity verification methods
          and allows purchasing of cryptocurrencies using a credit card. Because
          of this, some setup is required to make it work in the test
          environment (where real identity verification is not possible and real
          credit cards do not work).
        </Typography>
        <br />
      </>
    ),
  },
  {
    id: 'paying-with-the-quickbit-app-in-the-test-environment-2',
    title: 'Paying with the Quickbit app in the test environment',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Typography variant="body">
          There are multiple identity verification methods available, depending
          on the country used for logging in:
        </Typography>
        <ul>
          <li>
            <Typography variant="body">
              The method with the least setup required is Onfido (used for the
              United Kingdom), simply follow the login steps to setup an
              account. The document used for verification does not matter in the
              test environment. To make the account persist, this method will
              require you to set an email and password for future login
              attempts.
            </Typography>
          </li>
          <li>
            <Typography variant="body">
              A method requiring some setup is BankID (used for Sweden). This
              method requires a Swedish Test BankID which can be set up{' '}
              <a href="https://demo.bankid.com/">here</a>. Once setup, simply
              login using the personal number you generated, as well as the
              BankID app. Note that a test BankID app and production BankID app
              cannot be installed on the same device.
            </Typography>
          </li>
        </ul>
        <br />
      </>
    ),
  },
  {
    id: 'paying-with-the-quickbit-app-in-the-test-environment-3',
    title: 'Paying with the Quickbit app in the test environment',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Typography variant="body">
          Card details that can be used in the test environment are available{' '}
          <a href="https://developer.worldpay.com/products/access/card-payments/testing/">
            here
          </a>
          . Cards may not work with all setups, so we recommend using any of the
          following two cards:
        </Typography>
        <br />
        <Typography variant="body">Without 3DS:</Typography>
        <CodeBlock language="txt" code={cardExampleWithout3ds} />
        <Typography variant="body">With 3DS:</Typography>
        <CodeBlock language="txt" code={cardExampleWith3ds} />
        <Tag value="Note" iconName="info" variant="primary" size="large" />
        <Typography variant="body">
          Some specific values can be used to trigger errors in the card
          purchase flows. These values are available{' '}
          <a href="https://developer.worldpay.com/products/access/card-payments/testing/">
            here
          </a>
          .
        </Typography>
        <br />
      </>
    ),
  },
  {
    id: 'refunds',
    title: 'Refunds',
    page: 'integration',
    subPage: 'pay-orders',
    content: <Typography variant="heading2">Refunds</Typography>,
  },
  {
    id: 'refunds-1',
    title: 'Refunds',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Typography variant="body">
          Quickbit Pay checks that the correct amount is sent by the customer
          when purchasing your goods and services. In case the customer sends
          the wrong amount (or the amount is outside the bounds when using the{' '}
          <HashLink to="/docs/start#tolerance">Tolerance feature</HashLink>),
          the user can no longer complete the order and is asked to provide an
          address for Quickbit to refund the money to. This is all done in the
          checkout page, meaning no interaction is required from your side. The
          customer will be refunded in the same cryptocurrency they used to pay
          with.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'refunds-2',
    title: 'Refunds',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Typography variant="body">
          Once the customer has been refunded, we will send a callback event to
          you letting you know that this has happened. Since a refunded order
          cannot be completed, you can utilize this as a way of telling your
          customer to recreate the order.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'refunds-3',
    title: 'Refunds',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Typography variant="body">
          Manual refunds, i.e. the cases where a customer paid the correct
          amount but wishes to return their product in accordance with your
          refund policies, are supported both from the Quickbit backoffice and
          through the Quickbit Pay API. For the backoffice functionality, read
          more in the <HashLink to="/docs/backoffice">Backoffice</HashLink>{' '}
          section. For the API, the{' '}
          <InlineCode>{'POST /api/pay/v1/orders/{order_id}/refund'}</InlineCode>{' '}
          can be used for this purpose. Aside from the order ID to identify the
          order to refund, it also takes in the amount to refund. This can be
          less than or equal to the creation amount of the order, and should be
          supplied in the same currency as the order was created in.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'refunds-4',
    title: 'Refunds',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Typography variant="body">
          The way a refund is handled depends on the payment method used by the
          customer. If the customer used the Quickbit app, the refund will be
          handled instantly. If the customer used an external wallet, you will
          be provided with a refund URL that needs to be sent to the customer.
          This URL will allow the customer to provide an address for the refund
          to be sent to.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'refunds-5',
    title: 'Refunds',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Typography variant="body">
          Similarly to invalid amount refunds, you will receive a callback once
          the refund has been completed.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'payouts',
    title: 'Payouts',
    page: 'integration',
    subPage: 'pay-orders',
    content: <Typography variant="heading2">Payouts</Typography>,
  },
  {
    id: 'payouts-1',
    title: 'Payouts',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Typography variant="body">
          Payouts enable you as a merchant to send money in a cryptocurrency to
          your customer. This feature is currently in development and will be
          explained in more detail once it is available.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'order-statuses',
    title: 'Order statuses',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Typography variant="heading2">Order statuses</Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'order-statuses-1',
    title: 'Order statuses',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Typography variant="body">
          The following table shows the possible statuses for an order. These
          are the statuses you will see in the backoffice and in the API
          response when calling the{' '}
          <InlineCode>{'GET /api/pay/v1/order/<order_id>'}</InlineCode>{' '}
          endpoint.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'order-statuses-table',
    title: 'Order statuses table',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Table
          headers={[
            { label: 'Status', cellKey: 'status', sort: false },
            { label: 'Description', cellKey: 'description', sort: false },
            { label: 'Callback', cellKey: 'callback', sort: false },
          ]}
          rows={[
            {
              value: {
                status: 'PENDING',
                description:
                  'The order has been created. The user has been presented with the checkout page (showing QR code, crypto address, amount, etc.) No blockchain transaction has yet been noticed.',
                callback: '❌',
              },
            },
            {
              value: {
                status: 'NOTICED',
                description:
                  'A transaction for the order has been noticed on the blockchain, but it is not yet confirmed.',
                callback: '✅',
              },
            },
            {
              value: {
                status: 'COMPLETED',
                description: 'The order has been completed successfully',
                callback: '✅',
              },
            },
            {
              value: {
                status: 'CANCELLED',
                description:
                  'The order has been cancelled. It could have been manually cancelled by the user. It is also possible the order was cancelled due to an error or a timeout. Settings for timeout and cancelling on error are available in the backoffice.',
                callback: '✅',
              },
            },
            {
              value: {
                status: 'REFUNDING',
                description:
                  'A refund is in progress. This state occurs either when the customer has sent an invalid amount or when a manual refund has been initiated from the backoffice. In both cases the customer needs to visit the checkout link for the order and enter their crypto address to reclaim their funds.',
                callback: '❌',
              },
            },
            {
              value: {
                status: 'PARTIALLY_REFUNDED',
                description:
                  'The customer has been refunded for a portion of the order. This state happens when a manual refund has been completed for less than the full amount of the order.',
                callback: '✅',
              },
            },
            {
              value: {
                status: 'REFUNDED',
                description:
                  'The customer has been refunded for the order. This can be either due to an invalid amount or due to a manual refund initiated from the backoffice.',
                callback: '✅',
              },
            },
          ]}
        />
        <br></br>
      </>
    ),
  },
  {
    id: 'errors',
    title: 'Errors',
    page: 'integration',
    subPage: 'pay-orders',
    content: <Typography variant="heading2">Errors</Typography>,
  },
  {
    id: 'errors-1',
    title: 'Errors',
    page: 'integration',
    subPage: 'pay-orders',
    content: (
      <>
        <Typography variant="body">
          As mentioned in the{' '}
          <HashLink to="/docs/integration/pay-orders#callback-events">
            Callback events
          </HashLink>{' '}
          section, errors can occur when processing an order. Some of these
          errors are propagated directly from{' '}
          <a
            href="https://worldline.com/en/home/main-navigation/solutions/merchants/payment-orchestration.html"
            target="_blank"
            rel="noreferrer"
          >
            PaymentIQ
          </a>
          , our payment service provider, if the user pays using the Quickbit
          app. Others are generated by Quickbit. Below is a list of the possible
          error codes. For the errors generated by Quickbit, there are also
          matching descriptions.
          <CodeBlock code={errorsList} language="txt" />
        </Typography>
      </>
    ),
  },
];
